/**
 * Koło fortuny (PLAN §10.2): spin przez API (idempotentny), animacja prezentuje wynik serwera.
 * Tier „biedny" pokazany pozytywnie (kiełki). Brak przegranej.
 */
import Phaser from 'phaser';
import type { FortuneTier } from '@pieskowo/shared';
import { BaseScene } from '../../core/BaseScene.js';
import { UiKit } from '../../core/UiKit.js';
import { getSceneRouter } from '../../core/SceneRouter.js';
import { audioBus } from '../../core/AudioBus.js';
import { spinFortuneApi } from './authClient.js';
import {
  getOrCreateFortuneIdempotencyKey,
  loadFortuneResultLocal,
  saveFortuneResultLocal,
  targetAngleForTier,
  tierPresentation,
  WHEEL_SEGMENTS,
} from './fortuneLogic.js';

export class FortuneScene extends BaseScene {
  static readonly KEY = 'Fortune';

  private wheel!: Phaser.GameObjects.Container;
  private spinning = false;
  private resultShown = false;
  private resultText: Phaser.GameObjects.Text | null = null;
  private spinBtn: Phaser.GameObjects.Container | null = null;
  private confetti: Phaser.GameObjects.Arc[] = [];

  constructor() {
    super(FortuneScene.KEY);
  }

  preload(): void {
    this.preloadRealBg('bg_fortune');
  }

  create(): void {
    this.cameras.main.setBackgroundColor(0xffdab9);
    this.addRealBg('bg_fortune');
    audioBus.unlock();
    this.spinning = false;
    this.resultShown = false;

    const cx = this.gameW / 2;
    const cy = this.gameH / 2 - 20;

    this.add
      .text(cx, 40, 'Koło fortuny!', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '40px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.add
      .text(cx, 88, 'Zakręć i zobacz swój skarb startowy', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '20px',
        color: '#6b5f7a',
      })
      .setOrigin(0.5);

    this.wheel = this.buildWheel(cx, cy);
    // wskaźnik u góry
    this.add.triangle(cx, cy - 168, 0, 28, 22, 0, 44, 28, 0xff69b4).setOrigin(0.5, 0);

    this.resultText = this.add
      .text(cx, this.gameH - 140, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '26px',
        color: '#4a3f55',
        fontStyle: 'bold',
        align: 'center',
        wordWrap: { width: 900 },
      })
      .setOrigin(0.5);

    this.spinBtn = UiKit.button(this, {
      x: cx,
      y: this.gameH - 70,
      width: 280,
      height: 72,
      label: '🎡 Zakręć!',
      fill: 0xffb6c1,
      onClick: () => {
        void this.doSpin();
      },
    });

    // Jeśli już mamy lokalny wynik (reload w trakcie) — pokaż bez ponownego spinu UI
    const cached = loadFortuneResultLocal();
    if (cached) {
      this.wheel.setAngle(targetAngleForTier(cached.tier, 0) % 360);
      this.showResult(cached.tier, cached.grantZl, cached.balance, false);
    }
  }

  private buildWheel(cx: number, cy: number): Phaser.GameObjects.Container {
    const r = 150;
    const n = WHEEL_SEGMENTS.length;
    const seg = 360 / n;
    const parts: Phaser.GameObjects.GameObject[] = [];

    WHEEL_SEGMENTS.forEach((tier, i) => {
      const pres = tierPresentation(tier);
      const start = Phaser.Math.DegToRad(i * seg - 90);
      const end = Phaser.Math.DegToRad((i + 1) * seg - 90);
      const g = this.add.graphics();
      g.fillStyle(pres.color, 1);
      g.beginPath();
      g.moveTo(0, 0);
      g.arc(0, 0, r, start, end, false);
      g.closePath();
      g.fillPath();
      g.lineStyle(3, 0xffffff, 1);
      g.beginPath();
      g.moveTo(0, 0);
      g.arc(0, 0, r, start, end, false);
      g.closePath();
      g.strokePath();
      parts.push(g);

      const mid = Phaser.Math.DegToRad(i * seg + seg / 2 - 90);
      const lx = Math.cos(mid) * (r * 0.62);
      const ly = Math.sin(mid) * (r * 0.62);
      parts.push(
        this.add
          .text(lx, ly, pres.emoji, { fontSize: '28px' })
          .setOrigin(0.5),
      );
    });

    // hub
    parts.push(this.add.circle(0, 0, 28, 0xffffff).setStrokeStyle(4, 0xff69b4));
    parts.push(
      this.add
        .text(0, 0, '★', { fontSize: '24px', color: '#ff69b4' })
        .setOrigin(0.5),
    );

    const c = this.add.container(cx, cy, parts);
    return c;
  }

  private async doSpin(): Promise<void> {
    if (this.spinning || this.resultShown) return;
    this.spinning = true;
    if (this.spinBtn) {
      this.spinBtn.setAlpha(0.5);
    }
    this.resultText?.setText('Kręci się…');

    // Ten sam klucz idempotencji jest zachowywany między próbami (getOrCreateFortuneIdempotencyKey
    // czyta z localStorage) — ponowienie po błędzie trafia w tę samą operację na serwerze.
    const idem = getOrCreateFortuneIdempotencyKey();

    const api = await spinFortuneApi(idem);
    this.spinning = false;
    if (!this.sys.isActive()) return;

    if (!api.ok) {
      // Serwer NIE potwierdził zakręcenia — nie udajemy sukcesu i nie zapisujemy
      // fałszywego salda lokalnie. Pokazujemy błąd + retry, onboarding zostaje zablokowany.
      this.showSpinError();
      return;
    }

    const { tier, grantZl, balance } = api.data;
    saveFortuneResultLocal({ tier, grantZl, balance });

    const target = targetAngleForTier(tier, 5);
    this.spinning = true;
    await this.animateWheel(target);
    if (!this.sys.isActive()) return;
    this.spinning = false;
    this.showResult(tier, grantZl, balance, true);
  }

  /** Błąd spinu (offline/serwer) — pokaż komunikat i przycisk „Ponów", bez fałszywej wygranej. */
  private showSpinError(): void {
    this.resultText?.setText(
      'Ups! Nie udało się zakręcić. Sprawdź połączenie i spróbuj ponownie.',
    );

    if (this.spinBtn) {
      this.spinBtn.destroy(true);
      this.spinBtn = null;
    }

    this.spinBtn = UiKit.button(this, {
      x: this.gameW / 2,
      y: this.gameH - 70,
      width: 280,
      height: 72,
      label: 'Ponów',
      fill: 0xffb6c1,
      onClick: () => {
        void this.doSpin();
      },
    });
  }

  private animateWheel(targetAngle: number): Promise<void> {
    return new Promise((resolve) => {
      this.tweens.add({
        targets: this.wheel,
        angle: targetAngle,
        duration: 3200,
        ease: 'Cubic.easeOut',
        onComplete: () => resolve(),
      });
    });
  }

  private showResult(
    tier: FortuneTier,
    grantZl: number,
    balance: number,
    withFx: boolean,
  ): void {
    this.resultShown = true;
    const pres = tierPresentation(tier);
    this.resultText?.setText(`${pres.emoji} ${pres.messagePl}\nSaldo: ${balance} zł`);

    if (this.spinBtn) {
      this.spinBtn.destroy(true);
      this.spinBtn = null;
    }

    if (withFx) {
      this.burstConfetti();
    }

    UiKit.button(this, {
      x: this.gameW / 2,
      y: this.gameH - 70,
      width: 300,
      height: 72,
      label: 'Do miasteczka! →',
      fill: 0xb8e6d0,
      onClick: () => getSceneRouter().go('Town'),
    });

    // ciche użycie grantZl (unikaj unused gdy z cache)
    void grantZl;
  }

  private burstConfetti(): void {
    const cx = this.gameW / 2;
    const cy = this.gameH / 2;
    const colors = [0xffb6c1, 0xb8e6d0, 0xffd700, 0xbde0fe, 0xe6e0f8];
    for (let i = 0; i < 24; i++) {
      const c = this.add.circle(
        cx,
        cy,
        6 + Math.random() * 6,
        colors[i % colors.length]!,
      );
      this.confetti.push(c);
      const angle = Math.random() * Math.PI * 2;
      const dist = 80 + Math.random() * 200;
      this.tweens.add({
        targets: c,
        x: cx + Math.cos(angle) * dist,
        y: cy + Math.sin(angle) * dist,
        alpha: 0,
        duration: 900 + Math.random() * 500,
        ease: 'Cubic.easeOut',
        onComplete: () => c.destroy(),
      });
    }
  }
}

export default FortuneScene;
